// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent } from 'react';
import styled from '@emotion/styled';

import {
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Dialog from 'presentations/views/components/atoms/Dialog';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import { pricingPath } from 'presentations/routes/path';
import {
  black,
  gray03,
  gray04,
  primary,
} from 'presentations/views/styles/color';

interface Props {
  onClose: () => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const onClickSelectPlan = (e: SyntheticEvent<HTMLElement>) => {
    e.preventDefault();
    props.onClose();
    window.open(pricingPath());
  };

  return (
    <Dialog
      isOpen
      scroll
      titleComponent={
        <TitleContainer>
          <TitleIcon name="calendar-clock" color="primary" size="48px" />
          {t('title')}
          <Message>{t('message')}</Message>
          <CloseIconButton aria-label="close" onClick={props.onClose}>
            <CloseIcon name="close" size={fontSize(20)} />
          </CloseIconButton>
        </TitleContainer>
      }
      actions={
        <ActionsContainer>
          <ViewMore href={pricingPath()} onClick={onClickSelectPlan}>
            <Icon name="chevron-right" size={fontSize(20)} color="primary" />
            {t('view more')}
          </ViewMore>
          <Button
            color="primary"
            size="large"
            href={pricingPath()}
            onClick={onClickSelectPlan}
          >
            {t('button')}
          </Button>
        </ActionsContainer>
      }
      contentBorderColor={gray04}
    >
      <FeatureCotainer>
        <FeatureTitle>{t('feature title')}</FeatureTitle>
        <PlanList>
          {[
            t('features.0'),
            t('features.1'),
            t('features.2'),
            t('features.3'),
            t('features.4'),
            t('features.5'),
          ].map((row: any, index: number) => (
            <PlanListItem key={index}>
              <IconWrap name="check" size={fontSize(18)} />
              <PlanListItemContent>{row}</PlanListItemContent>
            </PlanListItem>
          ))}
          <PlanListItem>
            <PlanListItemContent>{t('more')}</PlanListItemContent>
          </PlanListItem>
        </PlanList>
      </FeatureCotainer>
    </Dialog>
  );
}

const TitleContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  font-size: ${fontSize(18)};

  ${mediaMax.tablet} {
    font-size: ${fontSize(16)};
  }
`;
const TitleIcon = styled(Icon)`
  margin-bottom: ${spacing(16)};
`;
const CloseIconButton = styled(IconButton)`
  position: absolute;
  right: 0;
  color: ${gray03};
`;
const CloseIcon = styled(Icon)``;

const Message = styled.div`
  margin-top: ${spacing(16)};
  white-space: pre-wrap;
  text-align: center;
  font-size: ${fontSize(14)};
  font-weight: initial;

  ${mediaMax.tablet} {
    margin-top: ${spacing(8)};
    font-size: ${fontSize(11)};
  }
`;

const FeatureCotainer = styled.div`
  padding: ${spacing(16)};

  ${mediaMax.tablet} {
    padding: ${spacing(16)} 0;
    font-size: ${fontSize(12)};
  }
`;
const FeatureTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(16)};
`;
const PlanList = styled.ul``;
const PlanListItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(8)};

  :last-of-type {
    margin-bottom: 0;
  }
`;
const PlanListItemContent = styled.span`
  text-align: left;
  margin-left: ${spacing(8)};
  width: fit-content;
`;
const IconWrap = styled(Icon)`
  color: ${primary};
  width: 18px;
  height: 18px;
`;

const ActionsContainer = styled.div`
  text-align: center;
`;
const ViewMore = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing(16)};
  color: ${black};
`;
